import { useEffect, useState, useContext, ReactElement } from "react";
import { ViewerApi, ThemeOptions, ThemeVariant } from "@bronte/viewer-api/src/main";
import { FONT_OPTIONS } from "../../shared/constants";
import { ThemeContext, IThemeContext } from "../../shared/ThemeContext";
import "./styles.scss";

interface ISettingsDrawer {
  viewerApi: ViewerApi;
  onClose: () => void;
}

interface IThemeVariant extends ThemeVariant {
  cssClass: string;
  label: string;
}

export const SettingsDrawer = ({ viewerApi, onClose }: ISettingsDrawer): ReactElement => {
  const { currentTheme, setCurrentTheme } = useContext(ThemeContext) as IThemeContext;
  const [themeVariants, setThemeVariants] = useState<IThemeVariant[]>([]);
  const [fontSize, setFontSize] = useState(currentTheme.fontSize);
  const [font, setFont] = useState(currentTheme.fontSelector);
  const [showHighlights, setShowHighlights] = useState(currentTheme.enableShowHide);

  const onUpdateTheme = (themeOptions?: Partial<ThemeOptions>): void => {
    const theme = { ...currentTheme, ...themeOptions };
    setCurrentTheme(theme);

    viewerApi.updateTheme(theme);
  };

  const handleVisibility = (): void => {
    const updatedShowHighlights = !showHighlights;

    setShowHighlights(updatedShowHighlights);
    onUpdateTheme({ enableShowHide: updatedShowHighlights });

    viewerApi.annotations().show(updatedShowHighlights);
  };

  useEffect(() => {
    const getThemeVariants = async (): Promise<void> => {
      let variants = await viewerApi.getThemeVariants();

      variants = variants?.map((variant: ThemeVariant) => ({
        ...variant,
        ...getVariantOptions(variant.variantName),
      }));

      setThemeVariants(variants as IThemeVariant[]);
    };

    getThemeVariants();
  }, []);

  const getVariantOptions = (variantName: string): { cssClass: string; label: string } => {
    switch (variantName) {
      case "Sepia":
        return {
          cssClass: "sepia",
          label: "Sepia theme",
        };
      case "Night":
        return {
          cssClass: "dark",
          label: "Dark theme",
        };
      default:
        return {
          cssClass: "light",
          label: "Light theme",
        };
    }
  };

  const updateFont = (fontValue: string): void => {
    setFont(fontValue);
    onUpdateTheme({ fontSelector: fontValue });
  };

  return (
    <div className="drawer settings-drawer">
      <div className="header">
        <h3 className="title">Settings</h3>
        <button className="close-button" aria-label="Close settings" onClick={onClose}>
          <div className="close-icon" />
        </button>
      </div>
      <div className="theme-button-row-label">Select Theme</div>
      <div className="theme-button-row">
        {themeVariants?.map((themeVariant: IThemeVariant) => (
          <button
            key={themeVariant.variantId}
            className={`action-button ${themeVariant.cssClass}`}
            aria-label={themeVariant.label}
            onClick={(): void => {
              onUpdateTheme({
                variantId: themeVariant.variantId,
              });
            }}
          >
            {themeVariant.variantName}
          </button>
        ))}
      </div>

      <div className="theme-button-row-label">Select Text Size</div>
      <div className="theme-button-row">
        <div className="range-slider">
          <input
            type="range"
            min="12"
            max="21"
            aria-label="font-size-slider"
            value={fontSize}
            onChange={(event): void => setFontSize(Number.parseInt(event.target.value))}
            onPointerUp={(): void => onUpdateTheme({ fontSize })}
          />
        </div>
      </div>

      <div className="theme-button-row-label">Select Font</div>
      <div>
        {FONT_OPTIONS.map((item) => (
          <label className="theme-font-option" key={`font_${item.value}`}>
            <input
              type="radio"
              name="font"
              value={item.value}
              checked={font === item.value}
              onChange={(e): void => updateFont(e.target.value)}
            />{" "}
            {item.label}
          </label>
        ))}
      </div>

      <div className="theme-button-row-label">Upper/Lowercase</div>
      <div className="theme-button-row">
        <button
          className={`action-button ${currentTheme.textTransform === "default" ? "active" : ""}`}
          aria-label="Default case"
          onClick={(): void => {
            onUpdateTheme({ textTransform: "default" });
          }}
        >
          Abcd
        </button>
        <button
          className={`action-button ${currentTheme.textTransform === "caps" ? "active" : ""}`}
          aria-label="All uppercase"
          onClick={(): void => {
            onUpdateTheme({ textTransform: "caps" });
          }}
        >
          ABCD
        </button>
      </div>

      <div className="theme-button-row-label">Select Line Spacing</div>
      <div className="theme-button-row">
        <button
          className={`action-button ${currentTheme.lineSpacing === "default" ? "active" : ""}`}
          aria-label="Default line height"
          onClick={(): void => {
            onUpdateTheme({ lineSpacing: "default" });
          }}
        >
          Default Line Spacing
        </button>
        <button
          className={`action-button ${currentTheme.lineSpacing === "wide" ? "active" : ""}`}
          aria-label="Wide line height"
          onClick={(): void => {
            onUpdateTheme({ lineSpacing: "wide" });
          }}
        >
          Wider Line Spacing
        </button>
        <button
          className={`action-button ${currentTheme.lineSpacing === "wider" ? "active" : ""}`}
          aria-label="Wider line height"
          onClick={(): void => {
            onUpdateTheme({ lineSpacing: "wider" });
          }}
        >
          Widest Line Spacing
        </button>
      </div>

      <div className="theme-button-row-label">Visibility</div>
      <div className="show-highlights-option">
        <label>
          <input
            type="checkbox"
            checked={showHighlights}
            onChange={handleVisibility}
            data-testid="show-highlights"
          />{" "}
          Show highlights
        </label>
      </div>

      <div className="theme-button-row-label">Continuous Scroll</div>
      <div className="theme-button-row">
        <button
          className={`action-button ${currentTheme.continuousScroll ? "active" : ""}`}
          aria-label="Scroll"
          onClick={(): void => {
            onUpdateTheme({ continuousScroll: true });
          }}
        >
          Scroll
        </button>
        <button
          className={`action-button ${!currentTheme.continuousScroll ? "active" : ""}`}
          aria-label="Swipe"
          onClick={(): void => {
            onUpdateTheme({ continuousScroll: false });
          }}
        >
          Swipe
        </button>
      </div>
    </div>
  );
};
